import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { MessageSquarePlus, Building2, Clock, Lightbulb, Bug, Sparkles, ChevronRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const CATEGORY_META = {
  feature_request: { label: 'Feature Request', icon: Lightbulb, darkColor: 'bg-violet-500/20 text-violet-400', lightColor: 'bg-violet-100 text-violet-700' },
  bug_report: { label: 'Bug Report', icon: Bug, darkColor: 'bg-red-500/20 text-red-400', lightColor: 'bg-red-100 text-red-700' },
  ux_improvement: { label: 'UX Improvement', icon: Sparkles, darkColor: 'bg-cyan-500/20 text-cyan-400', lightColor: 'bg-cyan-100 text-cyan-700' },
  general: { label: 'General', icon: MessageSquarePlus, darkColor: 'bg-slate-500/20 text-slate-300', lightColor: 'bg-gray-100 text-gray-600' },
};

const PRIORITY_META = {
  low: { label: 'Low', darkColor: 'bg-green-500/20 text-green-400', lightColor: 'bg-green-100 text-green-700' },
  medium: { label: 'Medium', darkColor: 'bg-amber-500/20 text-amber-400', lightColor: 'bg-amber-100 text-amber-700' },
  high: { label: 'High', darkColor: 'bg-red-500/20 text-red-400', lightColor: 'bg-red-100 text-red-700' },
};

const STATUS_COLOR = {
  new: 'bg-purple-500/20 text-purple-400',
  open: 'bg-blue-500/20 text-blue-400',
  in_progress: 'bg-yellow-500/20 text-yellow-400',
  waiting_on_customer: 'bg-orange-500/20 text-orange-400',
  waiting_on_support: 'bg-cyan-500/20 text-cyan-400',
  resolved: 'bg-green-500/20 text-green-400',
  closed: 'bg-slate-500/20 text-slate-400',
  reopened_by_customer: 'bg-red-500/20 text-red-400',
};

const FeedbackManagement = () => {
  const navigate = useNavigate();
  const { api } = useSuperAdmin();
  const { isDark = true } = useOutletContext() || {};
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const params = {};
        if (categoryFilter !== 'all') params.category = categoryFilter;
        const res = await api.get('/api/super-admin/feedback', { params });
        setFeedback(res.data);
      } catch (err) {
        console.error('Failed to fetch feedback:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFeedback();
  }, [api, categoryFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
            <MessageSquarePlus className="h-6 w-6 text-[#ffd108]" />
            Early Adopter Feedback
          </h1>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>
            {feedback.length} submission{feedback.length !== 1 ? 's' : ''} — click a row to open the ticket
          </p>
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className={`w-48 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}>
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="feature_request">Feature Requests</SelectItem>
            <SelectItem value="bug_report">Bug Reports</SelectItem>
            <SelectItem value="ux_improvement">UX Improvements</SelectItem>
            <SelectItem value="general">General</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {feedback.length === 0 ? (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardContent className="py-16 text-center">
            <MessageSquarePlus className={`h-12 w-12 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
            <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No feedback yet</p>
          </CardContent>
        </Card>
      ) : (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'border-slate-700 hover:bg-transparent' : 'border-gray-200 hover:bg-transparent'}>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Ticket</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Category</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Subject</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Tenant</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Status</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Priority</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Submitted</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {feedback.map((item) => {
                  const cat = CATEGORY_META[item.category] || CATEGORY_META.general;
                  const pri = PRIORITY_META[item.priority] || PRIORITY_META.medium;
                  const CatIcon = cat.icon;

                  return (
                    <TableRow
                      key={item.id}
                      data-testid={`feedback-row-${item.id}`}
                      className={`${isDark ? 'border-slate-700 hover:bg-slate-700/30' : 'border-gray-100 hover:bg-gray-50'} cursor-pointer`}
                      onClick={() => navigate(`/super-admin/tickets/${item.id}`)}
                    >
                      <TableCell className="text-[#ffd108] font-mono text-sm">
                        {item.ticket_number || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={isDark ? cat.darkColor : cat.lightColor}>
                          <CatIcon className="h-3 w-3 mr-1" />
                          {cat.label}
                        </Badge>
                      </TableCell>
                      <TableCell className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'} max-w-[300px] truncate`}>
                        {item.subject}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-3.5 w-3.5 text-[#ffd108]" />
                          <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{item.company_name || item.client_id}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLOR[item.status] || STATUS_COLOR.new}>
                          {(item.status || 'new').replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={isDark ? pri.darkColor : pri.lightColor}>{pri.label}</Badge>
                      </TableCell>
                      <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {item.created_at
                            ? formatDistanceToNow(new Date(item.created_at), { addSuffix: true })
                            : 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <ChevronRight className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FeedbackManagement;
